import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core";
import * as i18n from "../common/i18n";
import MaterialTable from 'material-table'
import { reactLocalStorage } from 'reactjs-localstorage';
import gql from 'graphql-tag';
import Message from "./Message";
import * as graphql from '../common/graphql';
import * as helper from "../common/helper";
import ReloadIcon from '@material-ui/icons/Replay';
import RouterIcon from '@material-ui/icons/Router';
import CheckIcon from '@material-ui/icons/Check';
import Chip from '@material-ui/core/Chip';
import { withIAM, isInRoles, ROLE_ADMIN, ROLE_READER, ROLE_OWNER, ROLE_ASSISTANT, ROLE_EDITOR } from "../common/iam";
import { AlertCause } from "../common/constants";
import moment from "moment-timezone";
import ChipLink from '../components/ChipLink';
import { CsvBuilder } from 'filefy';
import { EventCode, AlertStatus } from "../common/constants"
import LinearProgress from '@material-ui/core/LinearProgress';

const styles = theme => ({
});

const defaults = {
    id: true,
    created: false,
    status: false,
    cause: false,
    site: false,
    device: false,
    pageSize: 20,
};

const defaultsPropertyName = 'p71.alarms.defaults';

class Alerts extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            exporting: false,
            message: null,
            details: null,
            hideAfter: 3000,
            variant: "info",
            userSettings: reactLocalStorage.getObject(defaultsPropertyName, defaults),
        };

        this.tableRef = React.createRef();
    }

    default(id) {
        return this.state.userSettings[id] !== undefined ? this.state.userSettings[id] : defaults[id];
    }

    setDefault(id, value) {

        const newUserSettings = { ...this.state.userSettings };
        newUserSettings[id] = value;

        this.setState({ userSettings: newUserSettings }, () => {
            reactLocalStorage.setObject(defaultsPropertyName, newUserSettings);
        });
    }

    markDone(id) {
        new Promise(async (resolve, reject) => {
            try {

                const result = await graphql.clientWithToken(this.props.accessToken).mutate({
                    variables: {
                        "id": id,
                    },
                    mutation: gql`
                    mutation updateAlert($id: ID!) {

                        updateAlert: updateAlert(id: $id, input: {status: DONE}) {
                            id
                        }
                    
                    }
                `,
                });

                this.showMessage(i18n.get("alerts.message.markDone.success"), "success")
                this.tableRef.current.onQueryChange();
                resolve();
            } catch (error) {
                this.showMessage(i18n.get("alerts.message.markDone.error"), "error", error.message);
                reject(error);
            }
        })
    }

    mapField(column) {
        return column;
    }

    showMessage = (message, variant, details, hideAfter) => {
        this.setState({
            message: message,
            variant: variant ? variant : "info",
            hideAfter: hideAfter ? hideAfter : 3000,
            details: details ? details : null,
        })
    }

    closeMessage = () => {
        this.setState({
            message: null,
            details: null,
            hideAfter: 3000,
            variant: "info",
        })
    }

    exportCsv = async (columns, data) => {

        try {

            this.setState({
                exporting: true,
                progressValue: 0
            });

            const allAlerts = [];
            let totalCount = 0;
            const first = 100;
            let offset = 0

            this.showMessage("Export wird angestoßen. Bitte verlassen Sie die Seite nicht, solange der Exportvorgang läuft.", "info");

            do {

                const filters = []
                for (const column of columns) {
                    if (column.tableData.filterValue !== undefined && column.tableData.filterValue !== null) {
                        filters.push({
                            column: column,
                            field: column.field,
                            type: column.type,
                            comparator: column.comparator,
                            value: column.tableData.filterValue
                        })
                    }
                }
                const find = helper.whereString(filters, this.mapField);

                const result = await graphql.clientWithToken(this.props.accessToken).query({
                    query: gql`
                    query {
                        alerts(first: ${first}, offset: ${offset}, orderBy:[{column: "created" direction: DESC}], where:${find}) {
                            totalCount
                            elements {
                                ... on Alert {
                                    id
                                    device {
                                        uuid
                                    }
                                    created {
                                        X
                                    }
                                    cause
                                    status
                                    site
                                }
                            }
                        }
                    }
                `,
                    fetchPolicy: "no-cache"
                });
                totalCount = result.data.alerts.totalCount;
                offset = offset + first;
                for (const element of result.data.alerts.elements) {
                    allAlerts.push(
                        {
                            id: element.id,
                            device: element.device && element.device.uuid ? element.device.uuid : null,
                            created: element.created ? element.created.X : null,
                            cause: element.cause,
                            status: element.status,
                            site: element.site,
                        }
                    )
                }

                if (totalCount !== 0) {
                    this.setState({
                        exporting: true,
                        progressValue: (allAlerts.length / totalCount) * 100
                    });
                }
            } while (allAlerts.length < totalCount);

            const builder = new CsvBuilder('Alarme.csv')
                .setDelimeter(";")
                .setColumns(columns.map(columnDef => columnDef.title))
                .addRows(allAlerts.map(alert => { return [alert.device, EventCode[alert.cause], moment(alert.created, 'X').format("DD.MM.YYYY, HH:mm"), alert.site, AlertStatus[alert.status], alert.id] }))
                .exportFile();

            this.setState({
                exporting: false,
                progressValue: 0
            });
        } catch (error) {
            this.setState({
                exporting: false,
                progressValue: 0
            });
            this.showMessage(error.message, "error");
        }

    }

    render() {

        return (
            <div style={{ maxWidth: '100%' }}>
                {this.state.exporting && <div style={{ width: "100%" }}><LinearProgress value={this.state.progressValue} variant={"determinate"} /></div>}
                <MaterialTable
                    components={{
                        Cell: helper.defaultCell,
                        FilterRow: helper.defaultFilter
                    }}
                    icons={helper.tableIcons}
                    onChangeRowsPerPage={pageSize => { this.setDefault("pageSize", pageSize) }}
                    onChangeColumnHidden={data => { this.setDefault(data["field"], data["hidden"]) }}
                    tableRef={this.tableRef}
                    localization={i18n.getLocalization()}
                    columns={[
                        {
                            title: i18n.get("alerts.device"),
                            field: 'device',
                            cellStyle: {
                                minWidth: '150px',
                                maxWidth: '150px',
                            },
                            headerStyle: {
                                minWidth: '150px',
                                maxWidth: '150px',
                            },
                            hidden: this.default("device"),
                            sorting: true,
                            filtering: true,
                            customSort: () => 0,
                            editable: 'never',
                            render: (rowData) => {
                                if (rowData) {
                                    return <ChipLink
                                        icon={<RouterIcon />}
                                        link={{ page: i18n.get('menus.devices'), data: { uuid: rowData.device } }}
                                        key={rowData.device}
                                        label={`${rowData.device}`}
                                        style={{ marginRight: 8, marginBottom: 8 }}
                                    />
                                }
                            },
                        },
                        {
                            title: i18n.get("alerts.cause"),
                            field: 'cause',
                            hidden: this.default("cause"),
                            cellStyle: {
                                minWidth: '250px',
                                maxWidth: '250px',
                            },
                            headerStyle: {
                                minWidth: '250px',
                                maxWidth: '250px',
                            },
                            sorting: true,
                            filtering: true,
                            editable: 'never',
                            lookup: AlertCause,
                            render: rowData => {
                                if (rowData) {
                                    return helper.eventChipOf(rowData.cause, rowData.id);
                                } else {
                                    return null
                                }
                            }
                        },
                        {
                            title: i18n.get("alerts.created"),
                            field: 'created',
                            type: 'datetime',
                            hidden: this.default("created"),
                            sorting: true,
                            filtering: true,
                            editable: 'never',
                            cellStyle: {
                                minWidth: '300px',
                                maxWidth: '300px',
                            },
                            headerStyle: {
                                minWidth: '300px',
                                maxWidth: '300px',
                            },
                            filterComponent: helper.DateRangeFilter,
                        },
                        {
                            title: i18n.get("alerts.site"),
                            field: 'site',
                            hidden: this.default("site"),
                            cellStyle: {
                                minWidth: '400px',
                                maxWidth: '400px',
                            },
                            headerStyle: {
                                minWidth: '400px',
                                maxWidth: '400px',
                            },
                            sorting: true,
                            filtering: true,
                            editable: 'never',
                        },
                        {
                            title: i18n.get("alerts.status"),
                            cellStyle: {
                                minWidth: '120px',
                                maxWidth: '120px',
                            },
                            headerStyle: {
                                minWidth: '120px',
                                maxWidth: '120px',
                            },
                            hidden: this.default("status"),
                            field: "status",
                            sorting: true,
                            filtering: true,
                            comparator: 'IS',
                            lookup: AlertStatus,
                            render: rowData => {
                                return helper.alertStatusChipOf(rowData.status);
                            }
                        },
                        {
                            title: i18n.get("alerts.id"),
                            field: 'id',
                            hidden: this.default("id"),
                            sorting: true,
                            filtering: true,
                            editable: 'never',
                        },
                    ]}
                    data={query =>
                        new Promise(async (resolve, reject) => {

                            try {

                                const where = helper.whereString(query['filters'], this.mapField, ["device"]);
                                let orderBy = helper.orderByString(query, this.mapField, 'created', query["orderBy"] && query["orderBy"]["field"] === "device");
                                if (query["orderBy"] && query["orderBy"]["field"] === "device" && where === "[]") {
                                    where = '[{column: "created", comparator: GT, datetime: -1}]'
                                }

                                const result = await graphql.clientWithToken(this.props.accessToken).query({
                                    query: gql`
                                    query {
                                        alerts(first: ${query.pageSize}, offset: ${query.pageSize * query.page}, orderBy:${orderBy}, where:${where}) {
                                            totalCount
                                            elements {
                                                ... on Alert {
                                                    id
                                                    device {
                                                        uuid
                                                    }
                                                    created {
                                                        X
                                                    }
                                                    cause
                                                    status
                                                    site
                                               }
                                            }
                                        }
                                    }
                                `,
                                    fetchPolicy: "no-cache"
                                });
                                const searchResult = {
                                    page: query.page,
                                    data: result.data.alerts.elements.map(
                                        (element) => {
                                            return {
                                                id: element.id,
                                                device: element.device && element.device.uuid ? element.device.uuid : null,
                                                created: element.created && element.created.X ? moment(element.created.X, 'X').toDate() : null,
                                                cause: element.cause,
                                                status: element.status,
                                                site: element.site,
                                            };
                                        }),
                                    totalCount: result.data.alerts.totalCount
                                }
                                resolve(searchResult);
                            } catch (error) {
                                this.showMessage(error.message, "error");
                                reject(error);
                            }
                        })
                    }

                    actions={[
                        {
                            isFreeAction: true,
                            icon: () => { return <ReloadIcon /> },
                            tooltip: i18n.get("general.reload"),
                            onClick: async (event, rowData) => {
                                this.tableRef.current.onQueryChange();
                            }
                        },
                        rowData => ({
                            icon: () => { return <CheckIcon /> },
                            tooltip: rowData["status"] === "NEW" ? i18n.get("alerts.markDone") : '',
                            onClick: async (event, rowData) => {
                                this.markDone(rowData["id"])
                            },
                            disabled: rowData["status"] === "DONE",
                        }
                        )]}

                    title={i18n.get("alerts.title")}
                    options={{
                        exportButton: !this.state.exporting,
                        exportCsv: this.exportCsv,
                        filtering: true,
                        showTitle: false,
                        debounceInterval: 500,
                        search: false,
                        columnsButton: true,
                        pageSize: this.default("pageSize"),
                        pageSizeOptions: [5, 20, 50],
                        actionsColumnIndex: -1
                    }}

                />

                <Message
                    hideAfter={this.state.hideAfter}
                    onClose={this.closeMessage}
                    variant={this.state.variant}
                    message={this.state.message}
                    details={this.state.details}
                    open={this.state.message !== null}
                />
            </div>
        )
    }
}

Alerts.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withIAM(withStyles(styles, { withTheme: true })(Alerts))